import Phaser from 'phaser';
import type { AmbientFxPreset, AmbientLayerConfig, AmbientBlendMode } from '@/config/ambientFx.config';

/**
 * AmbientFxSystem — wielowarstwowy ambient particle effect (Step 3.B.2).
 *
 * Implementuje parallax-like depth effect z N warstw ParticleEmitter'ów.
 * Konfiguracja przez `AmbientFxPreset` (patrz `src/config/ambientFx.config.ts`).
 *
 * **World-relative spawn (sesja 28b fix):** emittery STOJĄ w punkcie (0,0) świata
 * z `scrollFactor=1` (default). Spawn zone (`emitZone.source` Rectangle) jest
 * mutowana per-frame na world-coords pasujące do aktualnego `cam.worldView` —
 * pas powyżej top edge'a viewportu, rozszerzony o 20% w boki dla wind drift.
 * Particles po spawnie żyją w world-space i scrollują naturalnie z kamerą,
 * więc gracz fizycznie przechodzi przez padający śnieg.
 *
 * (Wcześniejsza implementacja przesuwała `emitter.position` co frame, co
 * powodowało że alive particles RENDEROWAŁY SIĘ relative do emittera —
 * screen-following overlay zamiast world-relative.)
 *
 * **Wind:** wspólna sin-wave oscylacja (windFrequencyHz z presetu) modyfikuje
 * velocityX wszystkich alive particles przez `forEachAlive`. Per-layer amplitude
 * — bliska warstwa kołysze się mocniej, daleka prawie stoi.
 *
 * **Rotation:** per-particle prędkość obrotu losowana raz przy spawnie przez
 * `emitCallback`, trzymana w `WeakMap<Particle, number>` (nie nadpisuje
 * `particle.data` które Phaser używa internally dla EmitterOp easing).
 *
 * **Prewarm:** po stworzeniu emitterów wołamy `fastForward()` żeby cząstki
 * były już rozproszone po viewportcie gdy gracz wchodzi do biomu — zamiast
 * "napełniania" przez pierwsze 5-10s.
 *
 * **Cleanup:** `destroy()` niszczy wszystkie emittery + clearuje WeakMap.
 * Wołane przez WorldScene.cleanup() w SHUTDOWN handler, plus reset block
 * na początku create() (sesja 26d zombie state pattern).
 *
 * **Edge cases obsłużone:**
 *  - Brakująca tekstura w layer → console.warn + skip warstwy
 *  - Wszystkie warstwy z brakującymi teksturami → pusty `emitters[]`, no-op
 *  - Resize viewportu / zoom kamery → spawn rectangle czytany z `cam.worldView`
 *    każdą klatkę, automatycznie adaptuje się
 */
export class AmbientFxSystem {
  private readonly emitters: Phaser.GameObjects.Particles.ParticleEmitter[] = [];
  private readonly layerConfigs: AmbientLayerConfig[] = [];
  /** Per-layer spawn rectangle — referencja MUTOWANA per-frame zgodnie z `cam.worldView`. */
  private readonly spawnZones: Phaser.Geom.Rectangle[] = [];
  /**
   * Per-particle prędkość obrotu (deg/s). WeakMap żeby NIE kolidować z
   * `particle.data` używanym przez Phaser internally dla EmitterOp state
   * (alpha/scale/rotate easing). Automatyczny GC gdy particle umiera —
   * ale Phaser pooluje cząstki, więc Sane re-use: emitCallback ustawia
   * świeży rotSpeed na każde nowe życie particle'a.
   */
  private readonly rotSpeeds: WeakMap<Phaser.GameObjects.Particles.Particle, number>;
  private readonly startTimeMs: number;

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly preset: AmbientFxPreset,
  ) {
    this.rotSpeeds = new WeakMap();
    this.startTimeMs = scene.time.now;
    this.createEmitters();
    this.prewarm();
  }

  private createEmitters(): void {
    const cam = this.scene.cameras.main;

    for (const layer of this.preset.layers) {
      if (!this.scene.textures.exists(layer.textureKey)) {
        console.warn(`[AmbientFx] Pominięto warstwę — brak tekstury '${layer.textureKey}'`);
        continue;
      }

      // Spawn rectangle w WORLD COORDS. Init z aktualnego cam.worldView,
      // potem mutowany każdą klatkę w update(). Phaser sampluje z tego ref'a
      // przy każdym spawnie, więc mutacja jest widoczna live.
      const initialZone = new Phaser.Geom.Rectangle(
        cam.worldView.x - cam.worldView.width * 0.1,
        cam.worldView.y - 60,
        cam.worldView.width * 1.2,
        60,
      );

      // Per-layer rotation flag — używane przez emitCallback.
      const rotMin = layer.rotateSpeed.min;
      const rotMax = layer.rotateSpeed.max;
      const rotateEnabled = rotMax > 0;
      const rotSpeeds = this.rotSpeeds;

      // Używamy `Zones.RandomZone` instance (zamiast plain `{type, source}`
      // object literal) — Phaser typedef preferuje to jako EmitZoneData union
      // member. Rectangle ma metodę `getRandomPoint(point)` więc runtime
      // spełnia `RandomZoneSource`, ale typed def jest stricter (oczekuje
      // Vector2Like, Rectangle.getRandomPoint zwraca Geom.Point). Cast na
      // granicy — runtime OK, TS uciszony.
      const randomZone = new Phaser.GameObjects.Particles.Zones.RandomZone(
        initialZone as unknown as Phaser.Types.GameObjects.Particles.RandomZoneSource,
      );

      const emitter = this.scene.add.particles(0, 0, layer.textureKey, {
        emitZone: randomZone,
        // Pionowa prędkość spadania — bezpośrednio jako velocityY.
        speedY: { min: layer.speedY.min, max: layer.speedY.max },
        // Horyzontalny per-particle spread (niezależny od wind). Wind dodawany
        // w update() przez forEachAlive — tu nie ustawiamy speedX bo by się
        // dodawało jako stały odsetek.
        speedX: { min: -layer.speedXSpread, max: layer.speedXSpread },
        lifespan: layer.lifespanMs,
        scale: { min: layer.scale.min, max: layer.scale.max },
        alpha: layer.alphaPeak,
        // Initial rotation random — runtime spin robimy w update() ręcznie.
        rotate: rotateEnabled ? { min: 0, max: 360 } : 0,
        // Spawn pace: density per lifespan.
        frequency: Math.max(16, layer.lifespanMs / Math.max(1, layer.density)),
        quantity: 1,
        blendMode: toPhaserBlend(layer.blendMode),
        // emitCallback — losujemy rotSpeed PER SPAWN (Phaser pooluje cząstki,
        // więc to samo Particle dostaje świeży rotSpeed gdy "wskrzeszone").
        emitCallback: rotateEnabled
          ? (particle: Phaser.GameObjects.Particles.Particle) => {
              const speed = rotMin + Math.random() * (rotMax - rotMin);
              const signed = Math.random() < 0.5 ? -speed : speed;
              rotSpeeds.set(particle, signed);
            }
          : undefined,
      });

      emitter.setDepth(layer.depth);

      this.emitters.push(emitter);
      this.layerConfigs.push(layer);
      this.spawnZones.push(initialZone);
    }
  }

  /**
   * Symuluje upływ czasu żeby cząstki były już rozproszone po viewportcie
   * przy pierwszym rzeczywistym frame'cie. Bez prewarm gracz po wejściu do
   * biomu widziałby pusty świat napełniający się śniegiem przez ~lifespan ms.
   *
   * `fastForward(time, delta)`: Phaser symuluje N kroków × delta ms.
   * Dajemy 80% lifespan najdłuższej warstwy z step 33ms (≈30 FPS sym).
   */
  private prewarm(): void {
    if (this.emitters.length === 0) return;

    let maxLifespan = 0;
    for (const layer of this.layerConfigs) {
      if (layer.lifespanMs > maxLifespan) maxLifespan = layer.lifespanMs;
    }
    const warmTimeMs = Math.floor(maxLifespan * 0.8);
    for (const emitter of this.emitters) {
      emitter.fastForward(warmTimeMs, 33);
    }
  }

  /**
   * Per-frame tick — wywoływany z `WorldScene.update(time, dt)`.
   *
   * Robi 3 rzeczy:
   *  1. Aktualizuje `spawnZones[i]` rectangle żeby pas spawn'u był nad aktualnym
   *     viewportcie w world coords (auto-handle resize i camera scroll).
   *  2. Aplikuje wind force do alive particles (oscylacja sin × per-layer amp).
   *  3. Aplikuje rotation speed do alive particles (jeśli layer ma rotateSpeed>0).
   *
   * `dt` = sekundy od poprzedniego frame'a (NIE milisekundy — WorldScene.update
   * konwertuje deltaMs/1000 do dt przed wywołaniem).
   */
  update(time: number, dt: number): void {
    if (this.emitters.length === 0) return;

    const cam = this.scene.cameras.main;
    const view = cam.worldView;
    const elapsedSec = (time - this.startTimeMs) / 1000;

    // Wspólna faza wiatru dla wszystkich warstw (estetyczna synchronizacja).
    const windPhase = Math.sin(elapsedSec * 2 * Math.PI * this.preset.windFrequencyHz);

    // Spawn rectangle: 20% margines po bokach (na wind drift żeby nie były
    // widoczne edge'e wjeżdżania particles), zaczyna 60px nad top edge.
    // Aktualizowane per-frame — handle resize, zoom, scroll, wszystko z worldView.
    const spawnHeight = 60;
    const spawnTopY = view.y - spawnHeight;
    const spawnLeftX = view.x - view.width * 0.1;
    const spawnWidth = view.width * 1.2;

    for (let i = 0; i < this.emitters.length; i++) {
      const emitter = this.emitters[i]!;
      const layer = this.layerConfigs[i]!;
      const zone = this.spawnZones[i]!;

      // 1. Mutacja shared Rectangle — Phaser sampluje z tego ref'a przy spawnie.
      zone.x = spawnLeftX;
      zone.y = spawnTopY;
      zone.width = spawnWidth;
      zone.height = spawnHeight;

      // 2. Wind: per-particle dodajemy windPhase * amplitude * dt do velocityX.
      const windForce = windPhase * layer.windAmplitude;
      const maxAbsVelX = layer.speedXSpread + layer.windAmplitude * 1.5;

      // 3. Rotation: kręcimy alive particles z prędkością z WeakMap.
      const rotateEnabled = layer.rotateSpeed.max > 0;
      const rotSpeeds = this.rotSpeeds;

      emitter.forEachAlive((p) => {
        // Wind drift z clamp żeby cząstki nie wyleciały w kosmos po N cyklach.
        const next = p.velocityX + windForce * dt;
        p.velocityX = clamp(next, -maxAbsVelX, maxAbsVelX);

        if (rotateEnabled) {
          // rotSpeed był ustawiony w emitCallback. Fallback gdyby z jakichś
          // powodów cząstka nie przeszła przez callback (np. fastForward
          // w niektórych wersjach Phasera).
          let rotSpeed = rotSpeeds.get(p);
          if (rotSpeed === undefined) {
            rotSpeed = (layer.rotateSpeed.min + Math.random() * (layer.rotateSpeed.max - layer.rotateSpeed.min))
              * (Math.random() < 0.5 ? -1 : 1);
            rotSpeeds.set(p, rotSpeed);
          }
          const deltaDeg = rotSpeed * dt;
          // Ustawiamy ZARÓWNO angle (degrees) JAK I rotation (radians) — Phaser
          // renderer używa `rotation`, kopiowanie z `angle` mogłoby mieć 1-frame
          // lag w zależności od order updateów (sesja 28b review #5).
          p.angle += deltaDeg;
          p.rotation += deltaDeg * (Math.PI / 180);
        }
      }, this);
    }
  }

  /**
   * Niszczy wszystkie emittery + ich live particles. Wołane z:
   *  - WorldScene.cleanup() w SHUTDOWN handler (per-restart)
   *  - WorldScene reset block guard
   *
   * Idempotent — wielokrotne wywołanie nie powoduje błędu (emitter.destroy()
   * w Phaserze jest no-op po pierwszym wywołaniu).
   */
  destroy(): void {
    for (const emitter of this.emitters) {
      emitter.destroy();
    }
    this.emitters.length = 0;
    this.layerConfigs.length = 0;
    this.spawnZones.length = 0;
    // WeakMap nie wymaga jawnego clear — alive particles trzymane przez Phaser
    // pool są kandydatami do GC po destroy emittera; entry zniknie automatycznie.
  }
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function clamp(v: number, min: number, max: number): number {
  return v < min ? min : v > max ? max : v;
}

function toPhaserBlend(mode: AmbientBlendMode | undefined): Phaser.BlendModes {
  switch (mode) {
    case 'ADD':    return Phaser.BlendModes.ADD;
    case 'SCREEN': return Phaser.BlendModes.SCREEN;
    case 'NORMAL':
    default:       return Phaser.BlendModes.NORMAL;
  }
}
